import {
  assign, createMachine, DoneInvokeEvent, sendParent,
} from 'xstate';
import {
  InitialState, STATES, ACTIONS, GUARDS,
} from '../../interfaces/uploaderInterface';
import { EVENTS as WIDGET_EVENTS } from '../../interfaces/widgetInterface';
import { EVENTS as NETWORK_EVENTS } from '../../interfaces/networkInterface';
import { getUploadUrl, updateVideoFile } from '../../services/video.service';
import { uploadToS3 } from '../../services/s3.service';
import { ALL_VIDEO_FILE_TYPES } from '../../interfaces/videoInterface';
import { IWidgetConfigResponse } from '../../interfaces/configInterface';
import { ONE_MB_IN_BYTES } from '../../services/speedTest.service';
import { SentryTrackingFn } from '../../utils/sentry.utils';
import { ANSWER_TYPES } from '../../interfaces/jobInterface';
import { DEFAULT_VIDEO_DIMENSIONS } from '../../utils/constants.utils';

export const accUploaderMachine = createMachine<InitialState, DoneInvokeEvent<any>>(
  {
    predictableActionArguments: true,
    id: 'uploader',
    initial: STATES.IDLE,
    context: {
      isConnected: false,
      currentQuestion: null,
      file: null,
      signedUrl: '',
      uploadSpeed: 0,
      lastUploadChunk: {
        loaded: 0,
        timeStamp: 0,
      },
      videoId: '',
      videoFile: {
        started: true,
        completed: true,
        uploaded: false,
        filename: '',
        retakes: 0,
        videoLength: 0,
        uploadUrl: '',
        selectedTake: 0,
        originalDimensions: DEFAULT_VIDEO_DIMENSIONS,
      },
    },
    on: {
      [NETWORK_EVENTS.CONNECTION_CHANGED]: {
        actions: [ACTIONS.SET_CONNECTION],
      },
    },
    states: {
      [STATES.IDLE]: {
        always: { target: STATES.UPLOAD_CONTROLLER, cond: GUARDS.CAN_UPLOAD },
      },
      [STATES.UPLOAD_CONTROLLER]: {
        always: [
          { target: STATES.UPLOADED, cond: GUARDS.IS_ASSESSMENT_QUESTION }, // Will skip the upload to S3 in case it's an assessment
          { target: STATES.UPLOADING },
        ],
      },
      [STATES.UPLOADING]: {
        initial: STATES.GET_SIGNED_URL,
        states: {
          [STATES.GET_SIGNED_URL]: { // Call to the backend to get the signedUrl + backend should update the video to completed: true
            invoke: {
              id: 'getUploadUrl',
              src: (context) => getUploadUrl(context.videoId, context.videoFile?.filename || '', (context.file?.type || '').replace(/video\/(webm|mp4)/, ''), context.videoFile.originalDimensions || DEFAULT_VIDEO_DIMENSIONS),
              onDone: {
                actions: [ACTIONS.SET_SIGNED_URL],
                target: STATES.UPLOAD_TO_S3,
              },
              onError: [
                {
                  cond: (_, event) => event.data.response.status === 0,
                  target: `#uploader.${STATES.IDLE}`, // In case the internet connection dropped before we got the response from the backend
                  actions: [ACTIONS.SENTRY],
                },
                {
                  actions: [ACTIONS.SENTRY],
                  target: `#uploader.${STATES.UPLOADED}`, // In case the video already uploaded
                },
              ],
            },
          },
          [STATES.UPLOAD_TO_S3]: {
            on: {
              progress: {
                actions: [ACTIONS.UPDATE_UPLOAD_SPEED, ACTIONS.SEND_UPLOAD_PROGRESS],
              },
            },
            entry: [ACTIONS.INITIAL_UPLOAD_TIMESTAMP],
            invoke: {
              id: 'uploadToS3',
              src: ({ signedUrl, file }) => (callback) => uploadToS3(signedUrl, file as File, (e) => callback({ type: 'progress', data: e })),
              onDone: {
                target: `#uploader.${STATES.UPLOADED}`,
              },
              onError: {
                actions: [
                  (context, event) => SentryTrackingFn(event, { fileSize: context.file?.size, fileType: context.file?.type }, 'uploadToS3_failed', 'fatal'),
                  ACTIONS.SENTRY,
                ],
                target: `#uploader.${STATES.IDLE}`,
              },
            },
          },
        },
      },
      [STATES.UPLOADED]: {
        entry: [ACTIONS.SET_UPLOADED],
        invoke: {
          id: 'updateVideoFile',
          src: (context) => updateVideoFile(context.videoId, context.videoFile),
          onDone: [
            {
              actions: [ACTIONS.SET_VIDEO_FILE],
              target: STATES.FINISHED,
              cond: (context, event) => ((event.data.data.data as IWidgetConfigResponse).video.videos || [])[Number(context.currentQuestion) - 1].uploaded,
            },
            {
              actions: [ACTIONS.SET_VIDEO_FILE],
              target: STATES.IDLE,
            },
          ],
          onError: {
            actions: [ACTIONS.SENTRY],
            target: STATES.IDLE,
          },
        },
      },
      [STATES.FINISHED]: {
        type: 'final',
        entry: [ACTIONS.UPDATE_PARENT],
      },
    },
  },
  {
    actions: {
      [ACTIONS.SENTRY]: (context, event) => SentryTrackingFn(event, context, 'accUploaderMachine', 'fatal'),
      [ACTIONS.CONSOLE_LOG]: (context, event) => console.log(`[UPLOADER-${context.currentQuestion}]: `, context, event),
      [ACTIONS.SET_CONNECTION]: assign((_, event) => ({
        isConnected: event.data,
      })),
      [ACTIONS.SET_UPLOADED]: assign(({ videoFile }) => ({
        videoFile: { ...videoFile, uploaded: true },
      })),
      [ACTIONS.SET_SIGNED_URL]: assign((_, event) => ({
        signedUrl: event.data.data.data.uploadUrl || '',
      })),
      [ACTIONS.SET_VIDEO_FILE]: assign((context, event) => ({
        videoFile: ({ ...context.videoFile, ...(event.data.data.data as IWidgetConfigResponse).video.videos?.[Number(context.currentQuestion) - 1] }) as ALL_VIDEO_FILE_TYPES,
      })),
      [ACTIONS.INITIAL_UPLOAD_TIMESTAMP]: assign(({ lastUploadChunk }) => ({ lastUploadChunk: { ...lastUploadChunk, timeStamp: performance.now() } })),
      [ACTIONS.UPDATE_UPLOAD_SPEED]: assign(({ lastUploadChunk }, { data: { total, loaded, event: { timeStamp } } }) => ({
        uploadSpeed: total !== loaded ? Number((((loaded - lastUploadChunk.loaded) / ONE_MB_IN_BYTES) / ((timeStamp - lastUploadChunk.timeStamp) / 1000)).toFixed(2)) : 0,
        lastUploadChunk: { loaded, timeStamp },
      })),
      [ACTIONS.SEND_UPLOAD_PROGRESS]: sendParent(({ currentQuestion, uploadSpeed }, event) => ({ type: WIDGET_EVENTS.UPLOADER_PROGRESS, data: { questionNumber: currentQuestion, progressEvent: event.data, uploadSpeed } })),
      [ACTIONS.UPDATE_PARENT]: sendParent(({ currentQuestion, videoFile }) => ({ type: WIDGET_EVENTS.UPLOADER_FINISHED, data: { questionNumber: currentQuestion, videoFile } })),
    },
    guards: {
      [GUARDS.CAN_UPLOAD]: ({ isConnected, file, videoFile }) => isConnected && (!!file?.size || (!!videoFile.answerType && videoFile.answerType !== ANSWER_TYPES.VIDEO)),
      [GUARDS.IS_ASSESSMENT_QUESTION]: ({ videoFile }) => !!videoFile.answerType && videoFile.answerType !== ANSWER_TYPES.VIDEO,
    },
  },
);
